import { SteamDataChannel, isSteamNetworkingAvailable } from "./steamNetworking";
import { createSteamLobby, joinSteamLobby, SteamLobby } from "./steamLobby";
import { SteamClient } from "./connectionToServer";
import { Multiplayer } from "../../../shared/constants";

export const STEAM_VIRTUAL_PORT = 27015;

export class SteamConnectionPoller {
	private pollTimeout: any | null = null;
	private resolvePoll: ((peerSteamId: string | null) => void) | null = null;
	private lobby: SteamLobby | null = null;
	private channel: SteamDataChannel | null = null;
	private listenSocketOpen = false; //only the hosting side opens one
	private disposed = false;
	private lastError: string | null = null;
	constructor() { }

	setError(error: string) {
		this.lastError = error;
	}

	clearError() {
		this.lastError = null;
	}

	getError() {
		return this.lastError;
	}

	private fail(playerMessage: string, detail?: string): never {
		this.setError(playerMessage);
		throw new Error(detail ?? playerMessage);
	}

	async openHost(timeoutMS: number, onLobbyOpen: (lobbyId: string) => void) {
		this.clearError();
		try {
			// Check Steam availability
			const steamAvailable = await isSteamNetworkingAvailable();
			if (!steamAvailable) {
				this.fail("Steam isn't running");
			}

			const lobby = await createSteamLobby(2, 'friends_only');
			if (!lobby) {
				this.fail("Couldn't create a Steam lobby");
			}
			if (this.disposed) {
				//dispose ran before we owned this, so it left nothing
				lobby.leave();
				throw new Error("Disposed while creating the steam lobby");
			}
			this.lobby = lobby;

			const lobbyId = this.lobby.getLobbyId();
			if (!lobbyId) {
				this.fail("Couldn't create a Steam lobby", "Failed to get Steam lobby ID from created lobby");
			}

			const steamLobbyId = lobbyId.toString();
			console.log('Steam lobby created successfully with id ' + steamLobbyId);
			onLobbyOpen(steamLobbyId);

			// Create listen socket
			const listenSocket = await window.electronApi?.createListenSocket(STEAM_VIRTUAL_PORT);
			this.listenSocketOpen = !!listenSocket;
			if (this.disposed) {
				//disposed while the socket was being created, so it closed nothing
				throw new Error("Disposed while opening the listen socket");
			}
			if (!listenSocket) {
				this.fail("Couldn't open a connection port for Steam", "Failed to create listen socket");
			}

			const peerSteamId = await this.doPoll(timeoutMS);

			if (!peerSteamId) {
				if (this.disposed) {
					throw new Error("Poll cancelled by dispose");
				}
				this.fail("Nobody joined in time");
			}

			const channel = new SteamDataChannel(peerSteamId);
			try {
				await channel.connect();
			}
			catch (error) {
				this.fail("Couldn't connect to the other player", String(error));
			}
			if (this.disposed) {
				channel.close();
				throw new Error("Disposed while opening the channel");
			}
			this.channel = channel;
			console.log("steam connection made via steam lobby");
			return new SteamClient(channel, peerSteamId, null);
		}
		catch (error) {
			if (!this.disposed && !this.getError()) {
				this.setError("Couldn't set up the Steam game"); //unexpected: nothing named a reason
			}
			if (this.lobby) {
				this.lobby.leave();
				this.lobby = null;
			}
			await this.closeListenSocket();
			console.error("Error while polling for Steam connection:", error);
			return null;
		}
	}

	async connectToHost(lobbyId: string) {
		this.clearError();
		try {
			const steamLobby = await joinSteamLobby(lobbyId); //Move this to call site
			if (!steamLobby) {
				this.fail("That Steam invite is no longer available", "Could not join steam lobby " + lobbyId);
			}
			if (this.disposed) {
				steamLobby.leave();
				throw new Error("Disposed while connecting to Steam host lobby");
			}
			this.lobby = steamLobby;

			console.log('got lobby, now getting host');
			const hostId = await steamLobby.getLobbyHost()
			console.log(`host id: ${hostId} type ${typeof (hostId)}, toString: ${hostId.toString()}`);
			if (hostId === false) {
				this.fail("Couldn't find the host of that Steam game", "No lobby host for " + lobbyId);
			}
			console.log('got host, now connecting to peer');

			if (this.disposed) {
				throw new Error("Disposed while connecting to Steam host");
			}

			const hostSteamId = hostId.toString();
			console.log("[CLIENT] Connecting to host via Steam:", hostSteamId);

			// Initiate connection to host
			const success = await window.electronApi?.connectToPeer(hostSteamId, STEAM_VIRTUAL_PORT);
			if (!success) {
				this.fail("Couldn't reach the host over Steam", "Failed to initiate Steam connection");
			}
			if (this.disposed) {
				//the connection is initiated but no channel owns it yet
				await window.electronApi?.disconnectFromPeer(hostSteamId);
				throw new Error("Disposed while initiating connection to Steam host");
			}

			const channel = new SteamDataChannel(hostSteamId);
			try {
				await channel.connect();
			}
			catch (error) {
				this.fail("Couldn't connect to the host", String(error));
			}
			if (this.disposed) {
				channel.close();
				throw new Error("Disposed while opening the channel");
			}
			this.channel = channel;

			console.log("[CLIENT] Steam connection established!");
			const steamClient = new SteamClient(channel, hostSteamId, steamLobby);

			if (this.disposed) {
				throw new Error("Disposed while connecting to Steam host");
			}

			//The client owns the lobby from here, so we must not leave it out from under them
			this.lobby = null;
			return steamClient;
		}
		catch (error) {
			if (!this.disposed && !this.getError()) {
				this.setError("Couldn't join the Steam game"); //unexpected: nothing named a reason
			}
			if (this.lobby) {
				this.lobby.leave();
				this.lobby = null;
			}
			console.error("Error while connecting to Steam host:", error);
			return null;
		}
	}

	private async closeListenSocket() {
		if (!this.listenSocketOpen) return;
		this.listenSocketOpen = false;
		await window.electronApi?.closeListenSocket(STEAM_VIRTUAL_PORT);
	}

	async dispose() {
		this.disposed = true;
		this.finishPoll(null);
		if (this.lobby) {
			this.lobby.leave();
			this.lobby = null;
		}
		await this.closeListenSocket();
		this.channel = null;
	}

	/**
	 * Host side only: waits for the peer to come back to the listen socket this poller opened,
	 * which stays up for the whole session (see dispose) precisely so they can. Keeps trying
	 * until the deadline - a single failed attempt shouldn't end the session's chance to recover.
	 */
	async reconnect(timeoutMs: number = Multiplayer.SteamReconnectPollTimeout) {
		if (this.disposed) return false;
		if (!this.channel) {
			console.error("SteamConnectionPoller: No existing channel to reconnect");
			return false;
		}

		const deadline = Date.now() + timeoutMs;
		while (!this.disposed && Date.now() < deadline) {
			const peerSteamId = await this.doPoll(deadline - Date.now());
			if (!peerSteamId) return false; //deadline reached, or cancelled by dispose

			if (peerSteamId !== this.channel.steamId) {
				//We can only reconnect the channel we already have, which is bound to the player
				//who left. Someone else reaching us here has nothing to rejoin.
				console.warn(`SteamConnectionPoller: ${peerSteamId} connected, but we are waiting for ${this.channel.steamId}`);
			}

			try {
				await this.channel.connect();
				if (this.disposed) return false;
				return true;
			}
			catch (error) {
				//Connected at the socket level but the channel never opened. Go back to waiting
				//rather than giving up on the session.
				console.warn("SteamConnectionPoller: peer connected but the channel didn't come up", error);
			}
		}
		return false;
	}

	private doPoll(timeoutMs: number) {
		if (this.disposed) return Promise.resolve(null);
		const startTime = Date.now();

		return new Promise<string | null>((resolve) => {
			this.resolvePoll = resolve;

			const tick = async () => {
				const newPeer = await window.electronApi?.pollIncomingConnections(STEAM_VIRTUAL_PORT);
				if (this.resolvePoll !== resolve) return; //cancelled while we were polling
				if (newPeer) {
					this.finishPoll(newPeer);
				} else if (Date.now() - startTime > timeoutMs) {
					this.finishPoll(null);
				} else {
					this.pollTimeout = setTimeout(tick, 100);
				}
			};

			this.pollTimeout = setTimeout(tick, 100);
		});
	}

	private finishPoll(peerSteamId: string | null) {
		if (this.pollTimeout !== null) {
			clearTimeout(this.pollTimeout);
			this.pollTimeout = null;
		}
		const resolve = this.resolvePoll;
		this.resolvePoll = null;
		resolve?.(peerSteamId);
	}
}